<template>
  <DashboardLayout>

    <div class="d-flex align-items-center gap-2 mb-4 page-identifier">
      <p class="mb-0 main-identifier fw-semibold">Appraisal</p>
      <span class="material-symbols-outlined">keyboard_arrow_right</span>
      <p class="mb-0 sub-identifier">Update Appraisal</p>
    </div>

    <div class="d-flex justify-content-center mb-5">

      <div class="form-outer">
        <form @submit.prevent="step === totalSteps ? handleSubmit() : handleNext()">
          <div class="crud-form position-relative">

            <div class="form-inner mb-2">
              <div class="form-header d-flex justify-content-between align-items-center">
                <!-- <h1 class="mb-0">Update Appraisal (Step {{ step }} of {{ totalSteps }})</h1> -->
                <h1 class="mb-0">Update Appraisal</h1>
              </div>

              <div class="header-box d-flex justify-content-between align-items-center mb-3">
                <div class="progress-bar" :style="{
                  '--progress-width': progressWidth,
                  '--step-count': totalSteps
                }">
                  <!-- Row 1: Step Icons -->
                  <div class="step-icon" v-for="(s, i) in totalSteps" :key="'icon-' + i">
                    <div :class="['step', { active: step === i + 1, completed: step > i + 1 }]">
                      {{ step > i + 1 ? '✓' : i + 1 }}
                    </div>
                  </div>

                  <!-- Row 2: Progress Line -->
                  <div class="progress-line" :style="{ '--progress-width': progressWidth }" />

                  <!-- Row 3: Step Labels -->
                  <div class="step-label text-wrap" v-for="(s, i) in steps" :key="'label-' + i"
                    :class="{ active: step === i + 1 }">
                    {{ s.label }}
                  </div>
                </div>
              </div>

              <div v-if="step === 1">

                <div class="form-body">
                  <!-- <h2 class="field-head">A. General Information</h2> -->
                  <div class="row mb-3">
                    <div class="col-md-12 row form-group position-relative floating-label-group">
                      <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label for="year"
                          class="form-label">Sector</label></div>
                      <div class="col-md-9 input-right">
                        <select class="form-select" v-model="sector" @change="onSectorChange" required>
                          <option value="" disabled>- Select Sector -</option>
                          <option v-for="sector in sectors" :key="sector.id" :value="sector.id">{{ sector.code }} ({{
                            sector.name }})</option>
                        </select>
                      </div>
                    </div>

                    <div class="col-md-12 row form-group position-relative floating-label-group">
                      <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label for="year"
                          class="form-label">Originating M.D.A.</label></div>
                      <div class="col-md-9 input-right">
                        <select class="form-select" v-model="mda" @change="onMdaChange" required>
                          <option value="" disabled>- Select MDA -</option>
                          <option v-for="mda in mdas" :key="mda.id" :value="mda.id">{{ mda.name }}</option>
                        </select>
                        <input type="hidden" v-model="form.mdaName">
                      </div>
                    </div>

                    <div class="col-md-12 row form-group position-relative floating-label-group">
                      <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label for="year"
                          class="form-label">Approval Authority</label></div>
                      <div class="col-md-9 input-right">
                        <select class="form-select" v-model="form.approval_authority" @change="syncApprovalAuthority"
                          required>
                          <option value="" disabled>- Select Approving Authority -</option>
                          <option v-for="app in approval_authorities" :key="app.id" :value="app.id">{{ app.description
                          }}
                          </option>
                        </select>
                      </div>
                    </div>

                    <div class="col-md-12 row form-group position-relative floating-label-group">
                      <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label for="year"
                          class="form-label">Year</label></div>
                      <div class="col-md-9 input-right">
                        <select class="form-select" v-model="form.year" required>
                          <option value="" disabled>Select Year</option>
                          <option v-for="year in years" :key="year" :value="year">{{ year }}</option>
                        </select>
                      </div>
                    </div>

                    <div class="col-md-12 row form-group position-relative floating-label-group">
                      <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label for="year"
                          class="form-label">Quarter</label></div>
                      <div class="col-md-9 input-right">
                        <select class="form-select" v-model="form.quarter" required>
                          <option value="" disabled>- Select Quarter -</option>
                          <option value="1">Q1</option>
                          <option value="2">Q2</option>
                          <option value="3">Q3</option>
                          <option value="4">Q4</option>
                        </select>
                      </div>
                    </div>

                    <div class="col-md-12 row form-group position-relative floating-label-group">
                      <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label for="year"
                          class="form-label">Nature of Expenditure</label></div>
                      <div class="col-md-9 input-right">
                        <select class="form-select" v-model="form.nature_expenditure" required>
                          <option value="" disabled>- Select Economic Code Type -</option>
                          <option value="2">Recurrent</option>
                          <option value="3">Capital</option>
                        </select>
                      </div>
                    </div>

                    <div class="col-md-12 row form-group position-relative floating-label-group">
                      <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label for="year"
                          class="form-label">Amount Approved (₦)</label></div>
                      <div class="col-md-9 input-right">
                        <input id="amountApproved" type="text" class="form-control"
                          :value="formatCurrency(form.amount_approved)" inputmode="numeric"
                          @input="onAmountApprovedInput" placeholder="0.00" required />
                        <!-- <input id="amountApproved" type="text" class="form-control"
                          :value="formatNumber(form.amount_approved)" inputmode="numeric" pattern="[0-9,]*"
                          @keypress="onlyDigitsAndComma" @input="onAmountApprovedInput" placeholder="" required /> -->
                      </div>
                    </div>
                    <div class="col-md-12 row form-group position-relative floating-label-group">
                      <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label for="year"
                          class="form-label">Purpose of Request</label></div>
                      <div class="col-md-9 input-right">
                        <div class="col-md-12 input-right">
                          <textarea class="form-control" id="description" v-model="form.purpose_request" placeholder=""
                            rows="3" maxlength="200" required></textarea>
                          <small class="form-text text-muted char-counter" :title="`${remainingChars(form.purpose_request)} characters remaining`">
                            {{ (form.purpose_request || '').length }}/200 characters
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="step === 2">
                <!-- <h2 class="field-head">B. Budget</h2> -->

                <div class="row mb-3 budget-item-wrapper step-2 justify-content-center">
                  <div class="row content-wrapper section-border h-100 w-100">
                    <div class="col d-flex">
                      <div class="d-flex flex-column justify-content-center h-100 w-100">
                        <label class="form-label fw-bold fixed-label">Organizational Code</label>
                        <input type="text" class="form-control view-only-input text-center" v-model="form.orgCode"
                          readonly>
                      </div>
                    </div>
                    <div class="col d-flex">
                      <div class="d-flex flex-column justify-content-center h-100 w-100">
                        <label class="form-label fw-bold fixed-label">Approved Budget Amount (₦)</label>
                        <input type="hidden" v-model="form.app_budget_line" />
                        <!-- display the formatted string -->
                        <input type="text" class="form-control view-only-input text-center"
                          :value="Number(form.app_budget_line || 0).toLocaleString()" readonly />
                      </div>
                    </div>
                    <div class="col d-flex">
                      <div class="d-flex flex-column justify-content-center h-100 w-100">
                        <label class="form-label fw-bold fixed-label">Approved Released To Date (₦)</label>
                        <input type="text" class="form-control view-only-input text-center"
                          :value="Number(form.app_released_line || 0).toLocaleString()" readonly>
                      </div>
                    </div>
                    <div class="col d-flex">
                      <div class="d-flex flex-column justify-content-center h-100 w-100">
                        <label class="form-label fw-bold fixed-label">Balance (₦)</label>
                        <input type="hidden" v-model="form.balance_line" />
                        <!-- display the formatted string -->
                        <input type="text" class="form-control view-only-input text-center"
                          :value="Number(form.balance_line || 0).toLocaleString()" readonly />
                      </div>
                    </div>
                    <div class="col d-flex no-border">
                      <div class="d-flex flex-column justify-content-center h-100 w-100">
                        <label class="form-label fw-bold fixed-label ">Performance (%)</label>
                        <input type="text" class="form-control view-only-input text-center"
                          v-model="form.performance_line" readonly>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="table-responsive mt-4">
                  <table class="table table-bordered text-center align-middle">
                    <thead class="table-light">
                      <tr>
                        <th>Eco Code</th>
                        <th>Title</th>
                        <th>Approved Budget {{ form.budget_year_line }} (₦)</th>
                        <th>Approval Release To Date (₦)</th>
                        <th>Balance (₦)</th>
                        <!-- <th>Performance (%)</th> -->
                        <th>Amount Required (₦)</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(item, index) in economicItems" :key="index" :class="index % 2 === 0 ? 'row-even' : 'row-odd'">
                        <td>
                          {{ item.ecoCode }}
                        </td>
                        <td>
                          {{ item.title }}
                        </td>
                        <td>
                          {{ Number(item.approvedBudget || 0).toLocaleString() }}
                        </td>
                        <td>
                          {{ Number(item.releasedToDate || 0).toLocaleString() }}
                        </td>
                        <td>
                          {{ Number(item.balance || 0).toLocaleString() }}
                        </td>
                        <!-- <td>
                                        {{ item.performance }}%
                                      </td> -->
                        <td>
                          <input type="text" class="form-control text-center" :value="formatCurrency(item.amountRequired)"
                            inputmode="numeric" pattern="[0-9,]*" @keypress="onlyDigitsAndComma"
                            @input="e => onItemAmountInput(e, item)" placeholder="0" required />
                        </td>

                        <!-- <td>
                                        <input
                                          type="number"
                                          min="0"
                                          class="form-control form-control-sm"
                                          v-model.number="item.amountRequired"
                                          @input="validateBudgetStep"
                                        />
                                        {{ item.amountRequired }}
                                      </td> -->
                        <td>
                          <button type="button" class="btn btn-outline-danger btn-sm" @click="removeItem(index)">
                            <span class="iconify" data-icon="mdi:trash-can-outline"></span>
                          </button>
                          <input type="hidden" v-model="item.mdaLineItemBudgetId" />
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  <div class="mt-3">
                    <h5 class="mb-2" style="font-size: 16px;">Select Economic Line Item to Add:</h5>

                    <div class="d-flex justify-content-center">
                      <div class="d-flex align-items-end gap-2 flex-wrap">
                        <!-- Search Input with Label -->
                        <div class="d-flex flex-column" style="max-width: 160px;">
                          <label class="form-label mb-1 text-muted" style="font-size: 13px;">Filter Options</label>
                          <input type="text" v-model="searchTerm" placeholder="Search title/code"
                            class="form-control form-control-sm" />
                        </div>

                        <!-- Select Dropdown with Label -->
                        <div class="d-flex flex-column" style="max-width: 220px;">
                          <label class="form-label mb-1 text-muted" style="font-size: 13px;">Choose Line Item</label>
                          <select class="form-select form-select-sm" v-model="newEcoId" @change="onSelectEco">
                            <option value="" disabled>-- pick code --</option>
                            <option v-for="opt in filteredEcoOptions" :key="opt.id" :value="opt.id">
                              {{ opt.ecoCode }} — {{ opt.title }}
                            </option>
                          </select>
                        </div>

                        <!-- Add Button -->
                        <div class="d-flex flex-column">
                          <label class="invisible mb-1">Add</label>
                          <button type="button" class="btn btn-outline-primary btn-sm" :disabled="!newEcoId"
                            @click="addSelectedEco">
                            <span class="iconify" data-icon="mdi:plus"></span>
                            Add Item
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- <div class="mt-3 text-end">
                                  <button type="button" class="btn btn-outline-primary btn-sm" @click="addEconomicItem">
                                    <span class="iconify" data-icon="mdi:plus"></span> Add More
                                  </button>
                                </div> -->
                </div>
              </div>

              <div v-if="step === 3">
                <!--                <h2 class="field-head">C. Evaluation Of Expenditure Approval Limits</h2> -->

                <div class="row mb-3 align-items-center justify-content-center">
                  <div class="row form-group position-relative floating-label-group">
                    <div class="col-md-1 label-left d-flex justify-content-end align-items-center"><label for="year"
                        class="form-label">Year:</label></div>
                    <div class="col-md-1 input-right">
                      <input type="text" class="form-control view-only-input" v-model="form.year" placeholder=""
                        readonly>
                    </div>

                    <div class="col-md-1 label-left d-flex justify-content-end align-items-center"><label for="year"
                        class="form-label">Date:</label></div>
                    <div class="col-md-2 input-right">
                      <input type="text" class="form-control view-only-input" v-model="form.exp_date" placeholder=""
                        readonly>
                    </div>

                    <div class="col-md-2 label-left d-flex justify-content-end align-items-center"><label for="year"
                        class="form-label">Approval Authority:</label></div>
                    <div class="col-md-3 input-right">
                      <input type="text" class="form-control view-only-input" v-model="form.exp_app_auth" placeholder=""
                        readonly>
                    </div>

                    <div class="col-md-1 label-left d-flex justify-content-end align-items-center"><label for="year"
                        class="form-label">Quarter:</label></div>
                    <div class="col-md-1 input-right">
                      <input type="text" class="form-control view-only-input" v-model="form.exp_quarter" placeholder=""
                        readonly>
                    </div>
                  </div>

                  <div class="row g-4 m-0 mb-5 budget-item-wrapper justify-content-center step-3 align-items-stretch">
                    <!-- Left side-->
                    <div class="col-md-6">
                      <div class="col content-wrapper section-border h-100 w-100">
                        <div class="content d-flex">
                          <label for="year" class="form-label fw-bold fixed-label">Quarterly Authorized Amount</label>
                          <input type="hidden" v-model="form.exp_auth_amount">
                          <input type="text" class="form-control view-only-input text-end"
                            :value="'₦' + Number(form.exp_auth_amount || 0).toLocaleString()" placeholder="" readonly>
                        </div>

                        <div class="content d-flex">
                          <label for="year" class="form-label fw-bold fixed-label">Approved Recurrent Amount</label>
                          <input type="hidden" v-model="form.exp_rec_amount">
                          <input type="text" class="form-control view-only-input text-end"
                            :value="'₦' + Number(form.exp_rec_amount || 0).toLocaleString()" placeholder="" readonly>
                        </div>

                        <div class="content d-flex">
                          <label for="year" class="form-label fw-bold fixed-label">Approved Capital Amount</label>
                          <input type="hidden" v-model="form.exp_cap_amount">
                          <input type="text" class="form-control view-only-input text-end"
                            :value="'₦' + Number(form.exp_cap_amount || 0).toLocaleString()" placeholder="" readonly>
                        </div>

                        <div class="content d-flex">
                          <label for="year" class="form-label fw-bold fixed-label">Approval Balance</label>
                          <input type="hidden" v-model="form.exp_app_balance">
                          <input type="text" class="form-control view-only-input text-end"
                            :value="'₦' + Number(form.exp_app_balance || 0).toLocaleString()" placeholder="" readonly>
                        </div>
                      </div>
                    </div>

                    <!-- Right side-->
                    <div class="col-md-6">
                      <div class="col content-wrapper section-border h-100 w-100">
                        <div class="content d-flex">
                          <label for="year" class="form-label fw-bold fixed-label">Quarterly Authorization Limit
                            Number</label>
                          <input type="text" class="form-control view-only-input text-end"
                            v-model="form.exp_app_quarter" placeholder="" readonly>
                        </div>

                        <div class="content d-flex">
                          <label for="year" class="form-label fw-bold fixed-label">Number Already Approved In
                            Quarter</label>
                          <input type="text" class="form-control view-only-input text-end"
                            v-model="form.exp_limit_number" placeholder="" readonly>
                        </div>

                        <div class="content d-flex">
                          <label for="year" class="form-label fw-bold fixed-label">Quarterly Authorized Limit
                            Balance</label>
                          <input type="text" class="form-control view-only-input text-end"
                            v-model="form.exp_limit_balance" placeholder="" readonly>
                        </div>

                      </div>
                    </div>
                  </div>

                  <div
                    class="col-md-11 row form-group position-relative floating-label-group content-wrapper section-border">
                    <div class="col-md-1 label-left d-flex justify-content-end">
                      <label for="remark" class="form-label">Remark</label>
                    </div>
                    <div class="col-md-11 input-right">
                      <div class="col-md-12 input-right">
                        <textarea class="form-control" id="description" v-model="form.exp_remark" placeholder=""
                          rows="3" maxlength="200" required></textarea>
                        <small class="form-text text-muted char-counter" :title="`${remainingChars(form.exp_remark)} characters remaining`">
                          {{ (form.exp_remark || '').length }}/200 characters
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- <div v-if="step === 4">
                              <h2 class="field-head">D. Recommendation</h2>

                              <div class="row mb-3">

                                  <div class="col-md-12 row form-group position-relative floating-label-group">
                                    <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label for="year" class="form-label">Approval Balance</label></div>
                                    <div class="col-md-9 input-right">
                                      <input type="number" class="form-control" v-model="name" placeholder="" required>
                                    </div>
                                  </div>

                                  <div class="col-md-12 row form-group position-relative floating-label-group">
                                    <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label for="year" class="form-label">Sectoral Head Remark</label></div>
                                    <div class="col-md-9 input-right">
                                      <div class="col-md-12 input-right">
                                          <textarea
                                          class="form-control"
                                          id="description"
                                          v-model="description"
                                          placeholder=""
                                          rows="3"
                                          required
                                          ></textarea>
                                        </div>
                                    </div>
                                  </div>

                                  <div class="col-md-12 row form-group position-relative floating-label-group">
                                    <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label for="year" class="form-label">Director Of Budget Remark</label></div>
                                    <div class="col-md-9 input-right">
                                      <div class="col-md-12 input-right">
                                          <textarea
                                          class="form-control"
                                          id="description"
                                          v-model="description"
                                          placeholder=""
                                          rows="3"
                                          required
                                          ></textarea>
                                        </div>
                                    </div>
                                  </div>

                                  <div class="col-md-12 row form-group position-relative floating-label-group">
                                    <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label for="year" class="form-label">Permanent Secretary Remark</label></div>
                                    <div class="col-md-9 input-right">
                                      <div class="col-md-12 input-right">
                                          <textarea
                                          class="form-control"
                                          id="description"
                                          v-model="description"
                                          placeholder=""
                                          rows="3"
                                          required
                                          ></textarea>
                                        </div>
                                    </div>
                                  </div>

                                  <div class="col-md-12 row form-group position-relative floating-label-group">
                                    <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label for="year" class="form-label">Commissioner Remark</label></div>
                                    <div class="col-md-9 input-right">
                                      <div class="col-md-12 input-right">
                                          <textarea
                                          class="form-control"
                                          id="description"
                                          v-model="description"
                                          placeholder=""
                                          rows="3"
                                          required
                                          ></textarea>
                                        </div>
                                    </div>
                                  </div>
                              </div>
                            </div> -->

              <div class="mt-4">
                <div v-if="step === 1" class="d-flex justify-content-end">
                  <button type="button" class="btn btn-primary next-btn d-inline-flex align-items-center"
                    @click="handleNext" :disabled="loading" :aria-label="loading ? 'Loading…' : 'Next step'"
                    aria-live="polite">
                    <!-- Spinner appears only when loading = true -->
                    <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"
                      aria-hidden="true"></span>

                    <!-- Button text -->
                    <span class="me-2">{{ loading ? 'Loading...' : 'Next' }}</span>

                    <!-- Iconify arrow icon -->
                    <span class="iconify" data-icon="formkit:arrowright" style="font-size: 1.2em;"></span>
                  </button>
                </div>

                <div v-else-if="step < totalSteps" class="d-flex justify-content-between">
                  <button type="button" class="btn btn-secondary back-btn" @click="handleBack">
                    <span class="iconify" data-icon="formkit:arrowleft" style="font-size: 1.2em;"></span>
                    Previous
                  </button>

                  <button type="button" class="btn btn-primary next-btn d-inline-flex align-items-center"
                    @click="handleNext" :disabled="loading" :aria-label="loading ? 'Loading…' : 'Next step'"
                    aria-live="polite">
                    <!-- Spinner appears only when loading = true -->
                    <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"
                      aria-hidden="true"></span>

                    <!-- Button text -->
                    <span class="me-2">{{ loading ? 'Loading...' : 'Next' }}</span>

                    <!-- Iconify arrow icon -->
                    <span class="iconify" data-icon="formkit:arrowright" style="font-size: 1.2em;"></span>
                  </button>
                  <!-- <button type="button" class="btn btn-outline-primary next-btn" @click="handleNext">
                                  <span>Next</span>
                                  <span class="iconify" data-icon="formkit:arrowright" style="font-size: 1.2em;"></span>
                                </button> -->
                </div>



                <div v-else-if="step === totalSteps" class="d-flex justify-content-center gap-3">
                  <button type="button" class="btn btn-secondary back-btn" @click="handleBack">
                    <span class="iconify" data-icon="formkit:arrowleft" style="font-size: 1.2em;"></span>
                    Previous
                  </button>

                  <button type="submit" class="btn btn-primary submit-btn" :disabled="loading">

                    <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"
                      aria-hidden="true"></span>

                    {{ (loading ? 'Submitting...' : 'Submit') }}
                  </button>

                  <!-- <button
                                  type="submit"
                                  class="btn"
                                  :class="step === totalSteps ? 'btn-primary' : 'btn-success'"
                                  :disabled="loading"
                                >

                                  <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>


                                  {{ (loading ? 'Submitting...' : 'Submit') }}
                                </button> -->
                </div>
              </div>



            </div>


          </div>
        </form>
      </div>
    </div>

  </DashboardLayout>
</template>

<script setup>
import { ref, reactive, onMounted, computed, watch } from 'vue'
import axios from 'axios'
import DashboardLayout from '../layout/DashboardLayout.vue'
import Cookies from 'js-cookie';
import Swal from 'sweetalert2';
import { useRoute } from 'vue-router';

const approval_authorities = ref([])

const mdas = ref([])

const sectors = ref([])

const sector = ref('')
const mda = ref('')

const root_api_url = process.env.VUE_APP_API_ROOT_URL
const totalSteps = 3
const step = ref(1)
const loading = ref(false)

// Progress bar and step
const steps = [
  { label: 'General Information' },
  { label: 'Budget' },
  { label: 'Evaluation Of Expenditure Approval Limits' }
]

// const progressWidth = computed(() => {
//   const stepPercentage = {
//     1: '33.33%',
//     2: '66.66%',
//     3: '100%',
//   };
//   return stepPercentage[step.value] || '0%';
// });

const progressWidth = computed(() => {
  const total = steps.length;
  const percentPerStep = 100 / total; // each step adds this much
  return (step.value * percentPerStep) + '%';
});

// Reactive form data
const form = reactive({
  mda: '',
  mdaName: '',
  organizationCode: '',
  economicLineItemId: '',
  year: '',
  orgCode: '',
  app_budget_line: '',
  app_released_line: '',
  balance_line: '',
  performance_line: '',
  budget_year_line: '',
  nature_expenditure: '',
  approval_authority: '',
  approval_authority_id: '',
  approval_authority_name: '',
  amount_approved: '',
  purpose_request: '',
  exp_quarter: '',
  exp_date: '',
  exp_limit_number: '',
  exp_app_quarter: '',
  exp_limit_balance: '',
  exp_auth_amount: '',
  exp_cap_amount: '',
  exp_rec_amount: '',
  exp_app_balance: '',
  exp_remark: '',
  mdaLineItemBudgetId: '',
  exp_app_auth: '',
  quarter: '',
  fileTrackerNumber: ''
})

const initialForm = JSON.parse(JSON.stringify(form));

function resetAll() {
  // back to step 1mdaLineItemBudgetId
  step.value = 1;

  // clear selects
  sector.value = '';
  mda.value = '';
  mdas.value = [];
  approval_authorities.value = [];

  // restore form fields
  Object.assign(form, JSON.parse(JSON.stringify(initialForm)));

  // clear dynamic lists & controls
  economicItems.value = [];
  newEcoId.value = '';
  searchTerm.value = '';
}

const currentYear = new Date().getFullYear();
const years = ref(Array.from({ length: currentYear - 2023 + 1 }, (_, i) => currentYear - i));

const route = useRoute()
const id = route.params.id

const fetchMemo = async () => {
  const token = Cookies.get('auth_token');
  try {
    await axios.get(`${root_api_url}/appraisal/memo/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    // console.log('Memo data:', data);

  } catch (err) {
    console.error('Error fetching memo:', err)
    const iconHtmlError = `<span class="iconify" data-icon="material-symbols:cancel-outline-rounded" style="font-size: 100px; color: red;"></span>`
    Swal.fire({
      html: `${iconHtmlError}
      <h1 style="font-weight:600;">Error</h1>
      <p>${err?.response?.data?.errorMessage || err?.response?.data?.error || err?.response?.data?.message || 'Failed to load appraisal details. Please try again.'}</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    });
  }
}

// const mda_datas = ref('')

const fetchSectors = async () => {
  const token = Cookies.get('auth_token');
  try {
    const response = await axios.get(`${root_api_url}/eli/sector`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    sectors.value = response.data.map(sector => ({
      id: sector.id,
      code: sector.sectorCode,
      name: sector.description,
    }))

  } catch (error) {
    console.error('Failed to fetch sectors:', error)
  }
}

const fetchMDAs = async (sector_id) => {
  const token = Cookies.get('auth_token');
  try {
    const response = await axios.get(`${root_api_url}/eli/mda/all/${sector_id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    mdas.value = response.data.map(mda => ({
      id: mda.id,
      name: mda.name,
    }))

  } catch (error) {
    console.error('Failed to fetch roles:', error)
  }
}


const fetchApprovalAuthority = async (mda_id) => {
  const token = Cookies.get('auth_token');
  try {
    const response = await axios.get(`${root_api_url}/eli/app-auth/all/${mda_id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    approval_authorities.value = response.data.map(approval_authority => ({
      id: approval_authority.id,
      description: approval_authority.description
    }))

  } catch (error) {
    console.error('Failed to fetch roles:', error)
  }
}

const handleSubmit = async () => {
  const token = Cookies.get('auth_token');

  const iconHtmlSuccess = `<span class="iconify" data-icon="ci:circle-check" style="font-size: 100px; color: green;"></span>`
  const iconHtmlError = `<span class="iconify" data-icon="material-symbols:cancel-outline-rounded" style="font-size: 100px; color: red;"></span>`


  // if (totalRequired.value > parseInt(form.exp_app_balance, 10)) {
  //   Swal.fire({
  //     html: `${iconHtmlError}
  //     <h1 style="font-weight:600;">Error</h1>
  //     <p>Total amount required (${(totalRequired.value).toLocaleString()}) exceeds approval authority's balance (${(form.exp_app_balance).toLocaleString()})</p>`,
  //     showConfirmButton: true,
  //     showCloseButton: true,
  //     icon: undefined,
  //   });

  //   loading.value = false;

  //   return;
  // }

  const appraisalData = {
    originatingMda: form.mdaName,
    approvalAuthority: form.approval_authority_name,
    // natureOfExpenditure: form.nature_expenditure,
    natureOfExpenditure: 2,
    amountApproved: form.amount_approved,
    purposeOfRequest: form.purpose_request,
    isRequestInBudgetInd: 0,
    approvedBudgetAmount: form.app_budget_line,
    amtReleasedToDate: form.app_released_line,
    balance: form.balance_line,
    performance: parseInt(form.performance_line, 10),
    appraisalEconomicItems: economicItems.value.map(item => ({
      // mdaLineItemBudgetId: item.approvedBudget,
      ecoCode: item.ecoCode,
      title: item.title,
      mdaLineItemBudgetId: item.mdaLineItemBudgetId,
      amountReleased: item.releasedToDate,
      amountRequired: item.amountRequired,
      amountBudgeted: item.approvedBudget,
      balance: item.balance,
      year: form.year,
      quarter: form.quarter,

    })),
    quarter: form.quarter,
    budgetYear: form.year,
    appraisalUpdatedDate: form.exp_date,
    noOfAppLimitPerQtr: parseInt(form.exp_limit_number, 10),
    noOfAuthIssuedInQtr: parseInt(form.exp_app_quarter, 10),
    noOfAuthLeftInQtr: parseInt(form.exp_limit_balance, 10),
    qtrAuthLimitAmt: parseInt(form.exp_auth_amount, 10),
    approvedRecurrentAmount: parseInt(form.exp_rec_amount, 10),
    approvedCapitalAmount: parseInt(form.exp_cap_amount, 10),
    approvalBalance: parseInt(form.exp_app_balance, 10),
    evalRemarks: form.exp_remark,
    orgCode: form.orgCode,
    approvalAuthorityId: form.approval_authority_id,
    mdaId: mda.value,
    fileTrackerNumber: form.fileTrackerNumber,
    evalApprovedRecurrentAmount: parseInt(form.exp_rec_amount, 10),
    evalApprovedCapitalAmount: parseInt(form.exp_cap_amount, 10),
    evalApprovalBalance: parseInt(form.exp_app_balance, 10),
  }

  loading.value = true;

  try {
    await axios.post(`${root_api_url}/appraisal/appraisal`, appraisalData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    Swal.fire({
      html: `${iconHtmlSuccess} <h1 style="font-weight:600;">Success</h1> <p>Appraisal Updated Successfully</p> `,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    }).then(() => {
      // location.reload()
      // router.push({ name: 'GenerateAppraisal' })
      resetAll()
    });

  } catch (error) {
    // Swal.fire({
    //   html: `${iconHtmlError}
    //   <h1 style="font-weight:600;">Error</h1>
    //   <p>Failed to update appraisal</p>`,
    //   showConfirmButton: true,
    //   showCloseButton: true,
    //   icon: undefined,
    // });

    if (error.response) {
      Swal.fire({
        html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>${error?.response?.data?.errorMessage || error?.response?.data?.error || 'Unexpected error occurred while creating MDA Budget. Kindly contact administrator'}</p>`,
        showConfirmButton: true,
        showCloseButton: true,
        icon: undefined,
      });
    } else {
      Swal.fire({
        html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>Unexpected error occurred while creating MDA Budget. Kindly contact administrator</p>`,
        showConfirmButton: true,
        showCloseButton: true,
        icon: undefined,
      });
    }
  } finally {
    loading.value = false; // Stop loading
  }
}

const remainingChars = (val) => 200 - (val || '').length

function syncApprovalAuthority() {
  const sel = approval_authorities.value.find(a => a.id === form.approval_authority)
  if (sel) {
    form.approval_authority_id = sel.id
    form.approval_authority_name = sel.description
  } else {
    form.approval_authority_id = ''
    form.approval_authority_name = ''
  }
}


onMounted(() => {
  // fetchApprovalAuthority()
  fetchMemo(),
    fetchSectors(),
    document.title = "Update Appraisal"
})





//start all ecoli fecth new

const ecoOptions = ref([])
const filteredEcoOptions = computed(() =>
  ecoOptions.value.filter(item =>
    [item.ecoCode, item.title]
      .join(' ')
      .toLowerCase()
      .includes(searchTerm.value.toLowerCase())
  )
)

const economicItems = ref([])      // rows in your table
const searchTerm = ref('')      // for filtering the select
const newEcoId = ref(null)    // ID of the item selected in dropdown
const selectedEco = ref(null)    // full object for that ID

const fetchEcoOptions = async (mdaid, year, nature_expenditure) => {
  const token = Cookies.get('auth_token')
  try {
    const { data } = await axios.get(`${root_api_url}/appraisal/appraisal/budget/${mdaid}/${year}/${nature_expenditure}`, {
      headers: { Authorization: `Bearer ${token}` }
    })



    // console.log(data);

    ecoOptions.value = (data.ecoLineItemDtos || []).map(dto => ({
      id: dto.id,
      //ecoCode: dto.economicLineItemDto.economicCode,
      ecoCode: dto.economicCode,
      title: dto.title,
      approvedBudget: dto.amountBudgeted,
      releasedToDate: dto.amountReleased,
      balance: dto.balance,
      performance: dto.performance,
      mdaLineItemBudgetId: dto.id,
    }));

    // console.log(ecoOptions.value);

    form.orgCode = data.organizationCode;
    form.app_budget_line = data.amountBudgeted;
    form.app_released_line = data.amountReleased;
    form.balance_line = data.balance;
    form.performance_line = data.performance;
    form.budget_year_line = data.year;

  } catch (err) {
    console.error('Error loading ECO items:', err)
    throw err;
  }
}

function onSelectEco() {
  selectedEco.value = ecoOptions.value.find(e => e.id === newEcoId.value) || null
}

function addSelectedEco() {
  if (!selectedEco.value) return

  const iconHtmlError = `<span class="iconify" data-icon="material-symbols:cancel-outline-rounded" style="font-size: 100px; color: red;"></span>`

  // prevent duplicates
  const exists = economicItems.value.some(e => e.id === selectedEco.value.id)
  if (exists) {
    return Swal.fire({
      html: `${iconHtmlError}
      <h1 style="font-weight:600;">Error</h1>
      <p>This economic item is already added</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    });
  }

  economicItems.value.push({
    id: selectedEco.value.id,
    ecoCode: selectedEco.value.ecoCode,
    title: selectedEco.value.title,
    approvedBudget: selectedEco.value.approvedBudget,
    releasedToDate: selectedEco.value.releasedToDate,
    balance: selectedEco.value.balance,
    amountRequired: 0,
    mdaLineItemBudgetId: selectedEco.value.mdaLineItemBudgetId,
  })

  // reset dropdown
  newEcoId.value = null
  selectedEco.value = null
}

const removeItem = (index) => {
  economicItems.value.splice(index, 1)
}

//end

// Compute total available balance across all items
const totalBalance = computed(() =>
  economicItems.value.reduce((sum, item) => sum + item.balance, 0)
)

const totalRequired = computed(() =>
  economicItems.value.reduce((sum, item) => sum + (item.amountRequired || 0), 0)
)


function onMdaChange() {
  // console.log('MDA changed to', mda.value)
  const selected = mdas.value.find(item => item.id === mda.value)
  form.mdaName = selected ? selected.name : ''

  fetchApprovalAuthority(mda.value)

  // fetchMdaDetails(mda.value)
  // fetchEcoOptions(mda.value)
}

function onSectorChange() {
  // console.log('Sector changed to', sector.value)
  // fetchMdaDetails(mda.value)
  fetchMDAs(sector.value)
}

const fetchExpAppLImit = async (appauth, mda_id, quarter, year) => {
  const token = Cookies.get('auth_token')
  try {
    const { data } = await axios.get(`${root_api_url}/appraisal/appraisal/approval-authority-evaluation/${appauth}/${mda_id}/${quarter}/${year}`, {
      headers: { Authorization: `Bearer ${token}` }
    })



    // console.log(data);
    const today = new Date();
    const isoDate = today.toISOString().split('T')[0];


    // form.exp_year = data.year;
    form.exp_quarter = data.quarter;
    form.exp_date = isoDate;
    form.exp_app_auth = data.approvalAuthorityName;
    form.exp_limit_number = data.noOfAuthIssuedInQtr;
    form.exp_app_quarter = data.noOfAppLimitPerQtr;
    form.exp_limit_balance = data.noOfAuthLeftInQtr;
    form.exp_auth_amount = data.qtrAuthLimitAmt;
    form.exp_cap_amount = data.approvedCapitalAmt;
    form.exp_rec_amount = data.approvedRecurrentAmt;
    form.exp_app_balance = data.approvalBalance;

  } catch (err) {
    console.error('Error loading Expenditure items:', err)
    throw err;
  }
}

function validateStep2Amounts() {
  // Items where amountRequired is missing, null, undefined, or <= 0
  return economicItems.value
    .map((it, idx) => ({ ...it, _idx: idx }))
    .filter(it => !it.amountRequired || Number(it.amountRequired) <= 0);
}


const handleNext = async () => {
  const iconHtmlError = `<span class="iconify" data-icon="material-symbols:cancel-outline-rounded" style="font-size: 100px; color: red;"></span>`
  if (step.value === 1) {

    // loading.value = true
    // Check if all required fields are filled
    const emptyFields = [];

    if (!sector.value) emptyFields.push("Sector");
    if (!mda.value) emptyFields.push("MDA");
    if (!form.approval_authority) emptyFields.push("Approval Authority");
    if (!form.year) emptyFields.push("Year");
    if (!form.quarter) emptyFields.push("Quarter");
    if (!form.nature_expenditure) emptyFields.push("Nature of Expenditure");
    if (!form.amount_approved) emptyFields.push("Amount Approved");
    if (!form.purpose_request) emptyFields.push("Purpose of Request");

    if (emptyFields.length > 0) {
      Swal.fire({
        html: `${iconHtmlError}
          <h1 style="font-weight:600;">Error</h1>
          <p>The following field(s) are empty:</p>
          <ul style="text-align:left;">${emptyFields.map(f => `<li>${f}</li>`).join('')}</ul>`,
        showConfirmButton: true,
        showCloseButton: true,
        icon: undefined,
      });
      return;
    }

    // 2️⃣ Turn on loading spinner
    loading.value = true// Show loading icon/button

    try {
      await fetchEcoOptions(mda.value, form.year, form.nature_expenditure)


      // if (
      //   !form.orgCode ||
      //   !form.app_budget_line ||
      //   !form.app_released_line ||
      //   !form.balance_line ||
      //   !form.performance_line ||
      //   !form.budget_year_line
      // ) {
      //   Swal.fire({
      //     html: `${iconHtmlError}
      //       <h1 style="font-weight:600;">Error</h1>
      //       <p>Budget for the MDA or Year could not be found</p>`,
      //     showConfirmButton: true,
      //     showCloseButton: true,
      //     icon: undefined,
      //   });
      //   loading.value = false;
      //   return;
      // }

      // const isEmpty = (value) => value === null || value === undefined;

      // const missingFields = [];

      // if (isEmpty(form.orgCode)) missingFields.push("Organization Code (orgCode)");
      // if (isEmpty(form.app_budget_line)) missingFields.push("Approved Budget Line (app_budget_line)");
      // if (isEmpty(form.app_released_line)) missingFields.push("Released Line (app_released_line)");
      // if (isEmpty(form.balance_line)) missingFields.push("Balance Line (balance_line)");
      // if (isEmpty(form.performance_line)) missingFields.push("Performance Line (performance_line)");
      // if (isEmpty(form.budget_year_line)) missingFields.push("Budget Year Line (budget_year_line)");

      // if (missingFields.length > 0) {
      //   Swal.fire({
      //     html: `${iconHtmlError}
      //       <h1 style="font-weight:600;">Error</h1>
      //       <p>The following field(s) are missing: <strong>${missingFields.join(", ")}</strong></p>`,
      //     showConfirmButton: true,
      //     showCloseButton: true,
      //     icon: undefined,
      //   });
      //   loading.value = false;
      //   return;
      // }
      // Proceed to next step
      // step.value++;
    } catch (error) {
      console.error('API error:', error);
      Swal.fire({
        html: `${iconHtmlError}
          <h1 style="font-weight:600;">Error</h1>
          <p>Something went wrong while validating. Please try again.</p>`,
        showConfirmButton: true,
        showCloseButton: true,
        icon: undefined,
      });

      return;
    } finally {
      loading.value = false; // Stop loading
    }
  }
  if (step.value === 2) {

    if (!economicItems.value || economicItems.value.length === 0) {
      Swal.fire({
        html: `${iconHtmlError}
          <h1 style="font-weight:600;">Error</h1>
          <p>No Economic Line Item Selected</p>`,
        showConfirmButton: true,
        showCloseButton: true,
        icon: undefined,
      });

      return;
    }
    if (totalRequired.value > totalBalance.value) {
      Swal.fire({
        html: `${iconHtmlError}
          <h1 style="font-weight:600;">Error</h1>
          <p>Total required (${(totalRequired.value).toLocaleString()}) exceeds total available balance (${(totalBalance.value).toLocaleString()})</p>`,
        showConfirmButton: true,
        showCloseButton: true,
        icon: undefined,
      });

      return;
    }

    const missingAmounts = validateStep2Amounts();
    if (missingAmounts.length > 0) {
      Swal.fire({
        html: `${iconHtmlError}
          <h1 style="font-weight:600;">Error</h1>
          <p>The following line item(s) do not have an Amount Required (must be > 0):</p>
          <ul style="text-align:left;">
            ${missingAmounts.map(it => `<li>${it.ecoCode} — ${it.title}</li>`).join('')}
          </ul>`,
        showConfirmButton: true,
        showCloseButton: true,
        icon: undefined,
      });

      return;
    }


    loading.value = true

    try {
      await fetchExpAppLImit(form.approval_authority_id, mda.value, form.quarter, form.year)
    } catch (error) {
      console.error('API error:', error);
      Swal.fire({
        html: `${iconHtmlError}
          <h1 style="font-weight:600;">Error</h1>
          <p>Something went wrong while validating.</p>`,
        showConfirmButton: true,
        showCloseButton: true,
        icon: undefined,
      });
      return;
    } finally {
      loading.value = false; // Stop loading
    }
    if (totalRequired.value > parseInt(form.exp_app_balance, 10)) {
      Swal.fire({
        html: `${iconHtmlError}
          <h1 style="font-weight:600;">Error</h1>
          <p>Total amount required (${(totalRequired.value).toLocaleString()}) exceeds approval authority's balance (${(form.exp_app_balance).toLocaleString()})</p>`,
        showConfirmButton: true,
        showCloseButton: true,
        icon: undefined,
      });

      loading.value = false;

      return;
    }
  }

  if (step.value < totalSteps) step.value++
}

const handleBack = () => {
  if (step.value > 1) step.value--
}

// function onAmountRequiredChange(idx) {
//   const item = economicItems.value[idx];
//   const available = item.balance;

//   // if within balance: just subtract
//   item.balance = available - item.amountRequired;
//   return;

//   // console.log(item.balance);

//   // else: overflow
//   // const overflow = item.amountRequired - available;

//   // // clamp this item
//   // item.amountRequired = available;
//   // item.balance = 0;

//   // find the next ECO option that's not yet used
//   // const nextOpt = ecoOptions.value.find(
//   //   o => !economicItems.value.some(e => e.id === o.id)
//   // );

//   // if (!nextOpt) {
//   //   Swal.fire({
//   //     icon: 'error',
//   //     title: 'No more line items',
//   //     text: 'You have exceeded all available ECO items.'
//   //   });
//   //   return;
//   // }

//   // ② Insert a new ECO row immediately after the current one
//   // economicItems.value.splice(idx + 1, 0, {
//   //   id: nextOpt.id,
//   //   ecoCode: nextOpt.ecoCode,
//   //   title: nextOpt.title,
//   //   approvedBudget: nextOpt.approvedBudget,
//   //   releasedToDate: nextOpt.releasedToDate,
//   //   balance: nextOpt.balance - overflow,
//   //   amountRequired: overflow
//   // });
// }

watch(
  [() => mda.value, () => form.year],
  ([newMda, newYear], [oldMda, oldYear]) => {
    // only act if something actually changed
    if (newMda !== oldMda || newYear !== oldYear) {
      economicItems.value = []
      ecoOptions.value = []
      newEcoId.value = null
      searchTerm.value = ''
    }
  }
)


/**
 * Format any number with commas
 */
// function formatNumber(val) {
//   return Number(val || 0).toLocaleString();
// }

function formatCurrency(val) {
  if (val === '' || val === null) return '';
  const num = parseFloat(val).toFixed(2);
  return Number(num).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Handle Amount Approved input
 */
const MAX_SAFE_RAW = Number.MAX_SAFE_INTEGER.toString(); // "9007199254740991"

function onAmountApprovedInput(e) {
  let raw = e.target.value.replace(/\D/g, '');

  if (raw === '') {
    form.amount_approved = '';
    e.target.value = '';
    return;
  }

  // Prevent adding more digits than safe
  if (raw.length > MAX_SAFE_RAW.length ||
    (raw.length === MAX_SAFE_RAW.length && raw > MAX_SAFE_RAW)) {
    raw = MAX_SAFE_RAW; // trim to safe max
  }

  const num = parseInt(raw, 10) / 100;
  form.amount_approved = Number(num.toFixed(2));
  e.target.value = formatCurrency(form.amount_approved);
}

function onItemAmountInput(e, item) {
  // Allow sequential zeros but strip other non-digits
  let raw = (e.target.value || '').replace(/\D/g, '');

  if (raw === '') {
    item.amountRequired = 0;
    e.target.value = '';
    return;
  }

  // Prevent exceeding safe integer limit
  if (raw.length > MAX_SAFE_RAW.length ||
    (raw.length === MAX_SAFE_RAW.length && raw > MAX_SAFE_RAW)) {
    raw = MAX_SAFE_RAW;
  }

  const num = parseInt(raw, 10) / 100;
  item.amountRequired = Number(num.toFixed(2));
  e.target.value = formatCurrency(item.amountRequired);
}

/**
 * Block keys other than digits, comma, or navigation
 */
function onlyDigitsAndComma(e) {
  const k = e.key;
  if (
    (k >= '0' && k <= '9') ||
    k === ',' || k === '.' ||
    ['Backspace', 'ArrowLeft', 'ArrowRight', 'Delete', 'Tab'].includes(k)
  ) return;
  e.preventDefault();
}

/**
 * Handle the “Amount Approved” input:
 * - strip non‑digits
 * - update the numeric model
 * - re‑format the visible field
 */
// function onAmountApprovedInput(e) {
//   const digits = e.target.value.replace(/[^\d]/g, '');
//   form.amount_approved = digits === '' ? 0 : Number(digits);
//   e.target.value = formatNumber(form.amount_approved);
// }

// function onItemAmountInput(e, item) {
//   const digits = e.target.value.replace(/[^\d]/g, '')
//   item.amountRequired = digits === '' ? 0 : Number(digits)
//   e.target.value = formatNumber(item.amountRequired)
// }


</script>

<style scoped>
.view-only-input {
  border: none !important;
  background-color: transparent;
  padding-left: 0;
  cursor: default;
  pointer-events: none;
  color: #212529;
  font-size: 1rem;
  padding-right: 0;
  margin-right: 1rem;
  margin-bottom: 0.5rem !important;
}

.budget-item-wrapper {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  /* forces every .col to match the tallest */
}

.budget-item-wrapper .view-only-input,
.budget-item-wrapper input.view-only-input {
  flex: 1;
  /* inputs (and your Approval‐Authority <div>) all grow equally */
  /* for your Approval Authority div: */
  white-space: pre-wrap;
}

.dashboard-layout .budget-item-wrapper {
  margin-top: 20px;
  padding: 0 40px;
}

.dashboard-layout .content-wrapper {
  border: 1px solid var(--border);
  background-color: #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  padding: 1rem;
}

.dashboard-layout .section-border {
  /* padding: 1rem; */
  border-top: 3px solid #667eea !important;
}

.dashboard-layout .step-2 {
  padding: 0 20px;
}

.dashboard-layout .budget-item-wrapper.step-2 .col {
  border-right: 1px solid var(--border);
  /* box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05); */
}

.dashboard-layout .budget-item-wrapper.step-2 .col.no-border {
  border-right: none !important;
}

.dashboard-layout .budget-item-wrapper.step-3 .fixed-label {
  min-height: 80px;
  justify-content: flex-start;
  width: 350px;
  box-sizing: border-box;
  text-align: left;
  /* all: unset; */
  display: flex;
  margin-bottom: 0;
  flex-shrink: 0;
  margin-left: 1rem;
}

.dashboard-layout .budget-item-wrapper.step-3 .col {
  padding: 0;
}

.dashboard-layout .budget-item-wrapper.step-3 .content-wrapper {
  border: 1px solid var(--border);
  background-color: #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  /* padding: 1rem; */
}

.dashboard-layout .budget-item-wrapper.step-3 .content {
  border-bottom: 2px solid var(--border);
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  gap: 1rem;
  width: 100%;
  justify-content: space-between;
  align-items: center;
}

.dashboard-layout .budget-item-wrapper.step-3 label {
  font-size: 16px;
  padding: .375rem 0 !important;
  margin-bottom: 0px !important;
  padding-bottom: 0.5rem;
  font-weight: 600;
}

.dashboard-layout .budget-item-wrapper .col-md-3 {
  border: 1px solid #212529;
}

.dashboard-layout .budget-item-wrapper .col-md-3.no-border {
  border: none;
}

.dashboard-layout .budget-item-wrapper .col-md-3.no-border label {
  padding: .375rem .75rem;
  margin-bottom: 0px !important;
}

.dashboard-layout .budget-item-wrapper .col-md-2.no-border {
  border: none;
}

.dashboard-layout .budget-item-wrapper .col-md-2.no-border label {
  padding: .375rem .75rem;
  margin-bottom: 0px !important;
}

.dashboard-layout .fixed-label {
  display: flex;
  align-items: center;
  /* vertical center */
  justify-content: center;
  /* horizontal center */
  min-height: 4rem;
  width: 100%;
  padding: 0 0.25rem;
  box-sizing: border-box;
  text-align: center;
}
</style>
